var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _RemoteSelections_instances, _RemoteSelections_terminator, _RemoteSelections_rootBox, _RemoteSelections_index, _RemoteSelections_knownUsers, _RemoteSelections_watchers, _RemoteSelections_listeners, _RemoteSelections_followed, _RemoteSelections_updateFollowed, _RemoteSelections_syncUser, _RemoteSelections_watchUser, _RemoteSelections_addOwner, _RemoteSelections_removeOwner, _RemoteSelections_createEntry;
import { Arrays, asInstanceOf, isDefined, Listeners, Option, Terminator, UUID } from "@opendaw/lib-std";
import { Address } from "@opendaw/lib-box";
import { Pointers } from "@opendaw/studio-enums";
import { SelectionBox, UserInterfaceBox } from "@opendaw/studio-boxes";
import { FilteredRemoteSelection } from "./FilteredRemoteSelection";
/**
 * Read only index of every *other* user's selections in a live room.
 *
 * Mirrors {@link VertexSelection}'s two layer watch, but once per remote user instead of once for
 * the followed user, and indexes `(selectable, user)` pairs so that painting can ask synchronously
 * who else selects a vertex. The followed user is excluded (the local {@link FilteredSelection}
 * renders it), so with a single user the index stays empty and {@link ownersOf} is a miss against an
 * empty set.
 */
export class RemoteSelections {
    constructor(rootBox, followed) {
        _RemoteSelections_instances.add(this);
        _RemoteSelections_terminator.set(this, void 0);
        _RemoteSelections_rootBox.set(this, void 0);
        _RemoteSelections_index.set(this, void 0); // sorted on selectable address
        _RemoteSelections_knownUsers.set(this, void 0); // every user currently in the graph
        _RemoteSelections_watchers.set(this, void 0); // one per *watched* (remote) user
        _RemoteSelections_listeners.set(this, void 0);
        _RemoteSelections_followed.set(this, Option.None);
        __classPrivateFieldSet(this, _RemoteSelections_terminator, new Terminator(), "f");
        __classPrivateFieldSet(this, _RemoteSelections_rootBox, rootBox, "f");
        __classPrivateFieldSet(this, _RemoteSelections_index, Address.newSet(entry => entry.selectable.address), "f");
        __classPrivateFieldSet(this, _RemoteSelections_knownUsers, UUID.newSet(user => user.address.uuid), "f");
        __classPrivateFieldSet(this, _RemoteSelections_watchers, UUID.newSet(watcher => watcher.user.address.uuid), "f");
        __classPrivateFieldSet(this, _RemoteSelections_listeners, new Listeners(), "f");
        __classPrivateFieldGet(this, _RemoteSelections_terminator, "f").own(__classPrivateFieldGet(this, _RemoteSelections_rootBox, "f").users.pointerHub.catchupAndSubscribe({
            onAdded: (pointer) => {
                const user = asInstanceOf(pointer.box, UserInterfaceBox);
                __classPrivateFieldGet(this, _RemoteSelections_knownUsers, "f").add(user);
                __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_syncUser).call(this, user);
            },
            onRemoved: (pointer) => {
                const user = asInstanceOf(pointer.box, UserInterfaceBox);
                __classPrivateFieldGet(this, _RemoteSelections_knownUsers, "f").removeByKey(user.address.uuid);
                __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_syncUser).call(this, user);
            }
        }, Pointers.User));
        __classPrivateFieldGet(this, _RemoteSelections_terminator, "f").own(followed.catchupAndSubscribe(option => __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_updateFollowed).call(this, option)));
    }
    ownersOf(vertex) {
        const entry = __classPrivateFieldGet(this, _RemoteSelections_index, "f").getOrNull(vertex.address);
        return isDefined(entry) ? entry.owners.values() : Arrays.empty();
    }
    forEach(procedure) {
        __classPrivateFieldGet(this, _RemoteSelections_index, "f").forEach(entry => entry.owners
            .forEach(user => procedure({ selectable: entry.selectable, user })));
    }
    subscribe(listener) {
        return __classPrivateFieldGet(this, _RemoteSelections_listeners, "f").subscribe(listener);
    }
    catchupAndSubscribe(listener) {
        this.forEach(({ selectable, user }) => listener.onSelected(selectable, user));
        return this.subscribe(listener);
    }
    createFilteredSelection(filter, mapping) {
        return new FilteredRemoteSelection(this, filter, mapping);
    }
    terminate() {
        __classPrivateFieldGet(this, _RemoteSelections_watchers, "f").forEach(watcher => watcher.terminable.terminate());
        __classPrivateFieldGet(this, _RemoteSelections_watchers, "f").clear();
        __classPrivateFieldGet(this, _RemoteSelections_knownUsers, "f").clear();
        __classPrivateFieldGet(this, _RemoteSelections_index, "f").clear();
        __classPrivateFieldGet(this, _RemoteSelections_terminator, "f").terminate();
    }
}
_RemoteSelections_terminator = new WeakMap(), _RemoteSelections_rootBox = new WeakMap(), _RemoteSelections_index = new WeakMap(), _RemoteSelections_knownUsers = new WeakMap(), _RemoteSelections_watchers = new WeakMap(), _RemoteSelections_listeners = new WeakMap(), _RemoteSelections_followed = new WeakMap(), _RemoteSelections_instances = new WeakSet(), _RemoteSelections_updateFollowed = function _RemoteSelections_updateFollowed(current) {
    const previous = __classPrivateFieldGet(this, _RemoteSelections_followed, "f");
    __classPrivateFieldSet(this, _RemoteSelections_followed, current.isEmpty() ? Option.None : Option.wrap(current.unwrap()), "f");
    previous.ifSome(user => __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_syncUser).call(this, user));
    __classPrivateFieldGet(this, _RemoteSelections_followed, "f").ifSome(user => { if (!previous.contains(user)) {
        __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_syncUser).call(this, user);
    } });
}, _RemoteSelections_syncUser = function _RemoteSelections_syncUser(user) {
    const shouldWatch = __classPrivateFieldGet(this, _RemoteSelections_knownUsers, "f").hasKey(user.address.uuid) && !__classPrivateFieldGet(this, _RemoteSelections_followed, "f").contains(user);
    const watching = __classPrivateFieldGet(this, _RemoteSelections_watchers, "f").hasKey(user.address.uuid);
    if (shouldWatch === watching) {
        return;
    }
    if (shouldWatch) {
        __classPrivateFieldGet(this, _RemoteSelections_watchers, "f").add({ user, terminable: __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_watchUser).call(this, user) });
    }
    else {
        __classPrivateFieldGet(this, _RemoteSelections_watchers, "f").removeByKey(user.address.uuid).terminable.terminate();
    }
}, _RemoteSelections_watchUser = function _RemoteSelections_watchUser(user) {
    const boxMap = UUID.newSet(entry => entry.box.address.uuid);
    const subscription = user.selection.pointerHub.catchupAndSubscribe({
        onAdded: (pointer) => {
            // Read only overlay over a *remote* user's boxes: a dangling or concurrently deleted
            // selectable must be skipped, never panic, or the whole YSync transaction is rejected.
            const box = asInstanceOf(pointer.box, SelectionBox);
            box.selectable.targetVertex.ifSome(vertex => {
                const selectable = vertex;
                if (boxMap.add({ box, selectable })) {
                    __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_addOwner).call(this, selectable, user);
                }
            });
        },
        onRemoved: (pointer) => {
            const box = asInstanceOf(pointer.box, SelectionBox);
            const entry = boxMap.removeByKeyIfExist(box.address.uuid);
            if (isDefined(entry)) {
                __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_removeOwner).call(this, entry.selectable, user);
            }
        }
    }, Pointers.Selection);
    return {
        terminate: () => {
            subscription.terminate();
            boxMap.forEach(entry => __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_removeOwner).call(this, entry.selectable, user));
            boxMap.clear();
        }
    };
}, _RemoteSelections_addOwner = function _RemoteSelections_addOwner(selectable, user) {
    const entry = __classPrivateFieldGet(this, _RemoteSelections_index, "f").getOrNull(selectable.address) ?? __classPrivateFieldGet(this, _RemoteSelections_instances, "m", _RemoteSelections_createEntry).call(this, selectable);
    entry.owners.add(user);
    __classPrivateFieldGet(this, _RemoteSelections_listeners, "f").proxy.onSelected(selectable, user);
}, _RemoteSelections_removeOwner = function _RemoteSelections_removeOwner(selectable, user) {
    const entry = __classPrivateFieldGet(this, _RemoteSelections_index, "f").getOrNull(selectable.address);
    if (!isDefined(entry)) {
        return;
    }
    entry.owners.removeByKey(user.address.uuid);
    if (entry.owners.size() === 0) {
        __classPrivateFieldGet(this, _RemoteSelections_index, "f").removeByKey(selectable.address);
    }
    __classPrivateFieldGet(this, _RemoteSelections_listeners, "f").proxy.onDeselected(selectable, user);
}, _RemoteSelections_createEntry = function _RemoteSelections_createEntry(selectable) {
    const created = { selectable, owners: UUID.newSet(owner => owner.address.uuid) };
    __classPrivateFieldGet(this, _RemoteSelections_index, "f").add(created);
    return created;
};
